/* ═══════════════════════════════════════════════════════════════
   NotificationBell — Sidebar bell icon with unread badge + dropdown
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { TaskNotification, TeamMember } from '../../shared/types';
import { TaskService } from '../services/taskService';
import * as cache from '../services/cloudCache';

interface NotificationBellProps {
  memberId: string;
  collapsed: boolean;
  onNavigateTask?: (taskId: string) => void;
}

const TYPE_ICONS: Record<string, string> = {
  assigned: '📌',
  comment: '💬',
  status_change: '🔄',
  due_soon: '⏰',
  completed: '✅',
};

function NotificationBell({ memberId, collapsed, onNavigateTask }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!memberId) return;
    try {
      const all: TaskNotification[] = await TaskService.listTaskNotifications(memberId);
      // Sort newest first
      all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setNotifications(all);
    } catch {
      /* ignore */
    }
  }, [memberId]);

  // Poll every 30s + load on mount
  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unread = notifications.filter((n) => !n.read);

  const markAllRead = async () => {
    if (unread.length === 0) return;
    try {
      // Mark each notification as read via cache save
      for (const n of unread) {
        await TaskService.saveTaskNotification({ ...n, read: true });
      }
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: collapsed ? '10px 0' : '10px 16px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          fontSize: 13,
          color: 'var(--st)',
          position: 'relative',
        }}
        title={`${unread.length} unread notification${unread.length !== 1 ? 's' : ''}`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread.length > 0 && (
          <span
            style={{
              position: 'absolute',
              top: collapsed ? 6 : 6,
              right: collapsed ? 12 : undefined,
              left: collapsed ? undefined : 28,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            {unread.length > 99 ? '99+' : unread.length}
          </span>
        )}
        {!collapsed && <span>Notifications</span>}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: collapsed ? 56 : 0,
            width: 320,
            maxHeight: 420,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid var(--bd)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx)' }}>
              Notifications{' '}
              {unread.length > 0 && (
                <span style={{ color: 'var(--tm)', fontWeight: 400 }}>
                  ({unread.length} unread)
                </span>
              )}
            </span>
            {unread.length > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--ac)',
                  fontSize: 11,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--tm)', fontSize: 12 }}>
                No notifications
              </div>
            ) : (
              notifications.slice(0, 50).map((n) => (
                <div
                  key={n.id}
                  onClick={() => {
                    if (onNavigateTask) onNavigateTask(n.taskId);
                    setOpen(false);
                  }}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--bd)',
                    background: n.read ? 'transparent' : 'rgba(var(--acr), 0.06)',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    transition: 'background .15s',
                  }}
                >
                  <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>
                    {TYPE_ICONS[n.type] || '📋'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--tx)',
                        lineHeight: 1.4,
                        fontWeight: n.read ? 400 : 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {n.message}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 3 }}>
                      {formatTime(n.createdAt)}
                    </div>
                  </div>
                  {!n.read && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: 'var(--ac)',
                        flexShrink: 0,
                        marginTop: 5,
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { NotificationBell };
